import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { NAV, COMPANY } from "@/lib/site";
import { Menu, X, Phone, ChevronDown } from "lucide-react";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => { setOpen(false); setMegaOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="hidden md:flex items-center justify-between px-6 lg:px-10 py-2 text-xs uppercase tracking-[0.2em] border-b border-white/5 text-zinc-400">
        <span>{COMPANY.region}</span>
        <div className="flex items-center gap-6">
          <a href={COMPANY.phoneHref} className="hover:text-white transition-colors" data-testid="header-phone-top">{COMPANY.phone}</a>
          <span>{COMPANY.hours}</span>
        </div>
      </div>

      <div className="flex items-center justify-between px-6 lg:px-10 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
          <div className="w-9 h-9 border border-white/20 flex items-center justify-center">
            <span className="font-display text-lg leading-none text-[#FF5722]">A</span>
          </div>
          <div className="leading-tight">
            <div className="font-display text-lg tracking-tight">AMERICAN ARMOR</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400">Coatings · TN</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.primary.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.mega && setMegaOpen(true)}
              onMouseLeave={() => item.mega && setMegaOpen(false)}
            >
              <NavLink
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm uppercase tracking-widest font-body font-semibold flex items-center gap-1 transition-colors ${
                    isActive ? "text-white" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {item.label}
                {item.mega && <ChevronDown className="w-3 h-3" />}
              </NavLink>

              {item.mega && megaOpen && (
                <div
                  className="absolute left-0 top-full pt-4"
                  data-testid="mega-menu"
                >
                  <div className="bg-black border border-white/10 w-[560px] p-6 grid grid-cols-1 gap-1">
                    <div className="eyebrow mb-3">Coatings & Prep</div>
                    {item.mega.map((sub) => (
                      <Link
                        key={sub.to}
                        to={sub.to}
                        className="group flex items-start justify-between gap-6 py-3 border-b border-white/5 hover:bg-white/[0.03] px-2 transition-colors"
                        data-testid={`mega-${sub.to}`}
                      >
                        <div>
                          <div className="font-display text-base tracking-tight group-hover:text-[#FF5722] transition-colors">{sub.label}</div>
                          <div className="text-xs text-zinc-400 mt-0.5">{sub.desc}</div>
                        </div>
                        <div className="text-[#FF5722] opacity-0 group-hover:opacity-100 transition-opacity">→</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/quote" className="hidden md:inline-flex btn-primary" data-testid="header-cta-quote">
            <Phone className="w-4 h-4" />
            Request a Quote
          </Link>
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="header-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black border-t border-white/10" data-testid="mobile-nav">
          <div className="flex flex-col p-6 gap-1">
            {NAV.primary.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="py-3 border-b border-white/5 font-display text-xl tracking-tight text-white"
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/quote" className="btn-primary mt-6 justify-center" data-testid="mobile-cta-quote">Request a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
};
